import {
  slugifyKbTitle,
  type CreateQueueRequest,
  type UpdateQueueRequest,
} from '@gb-ticket/shared-types';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TEAMS_ADMIN_PATH } from '@/platform/lib/admin';

const NONE = '__none__';

export type QueueFormValues = {
  id?: string;
  name: string;
  slug: string;
  teamId: string | null;
  defaultAssigneeId: string | null;
  isActive: boolean;
};

type AgentOption = { id: string; displayName: string };
type TeamOption = { id: string; name: string; isActive: boolean };

type QueueFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: QueueFormValues | null;
  busy: boolean;
  agents: AgentOption[];
  teams: TeamOption[];
  /** When true, show link to /teams (platform.users:manage). */
  canManageTeams?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: {
    create?: CreateQueueRequest;
    update?: UpdateQueueRequest;
  }) => Promise<void>;
};

export function suggestQueueSlug(name: string): string {
  const slug = slugifyKbTitle(name);
  return slug === 'artikel' ? 'warteschlange' : slug;
}

export function QueueFormDialog({
  open,
  mode,
  initial,
  busy,
  agents,
  teams,
  canManageTeams = false,
  onOpenChange,
  onSubmit,
}: QueueFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open ? (
          <QueueFormFields
            key={`${mode}:${initial?.id ?? 'new'}`}
            mode={mode}
            initial={initial}
            busy={busy}
            agents={agents}
            teams={teams}
            canManageTeams={canManageTeams}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function QueueFormFields({
  mode,
  initial,
  busy,
  agents,
  teams,
  canManageTeams = false,
  onOpenChange,
  onSubmit,
}: Omit<QueueFormDialogProps, 'open'>) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [teamId, setTeamId] = useState(initial?.teamId ?? NONE);
  const [defaultAssigneeId, setDefaultAssigneeId] = useState(initial?.defaultAssigneeId ?? NONE);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const activeTeams = useMemo(() => {
    const active = teams.filter((team) => team.isActive);
    const currentId = initial?.teamId;
    if (!currentId) {
      return active;
    }
    const current = teams.find((team) => team.id === currentId);
    if (current && !current.isActive && !active.some((team) => team.id === current.id)) {
      return [current, ...active];
    }
    return active;
  }, [teams, initial]);
  const canSubmit = name.trim().length >= 2 && slug.trim().length >= 2;

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'create' ? 'Warteschlange hinzufügen' : 'Warteschlange bearbeiten'}
        </DialogTitle>
        <DialogDescription>
          Name und Kurzname erscheinen als Spalte auf dem Board. Team und Standard-Bearbeiter
          steuern Routing und Benachrichtigungen.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="queue-name">Name</Label>
          <Input
            id="queue-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugTouched) {
                setSlug(suggestQueueSlug(next));
              }
            }}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="queue-slug">Kurzname (Slug)</Label>
          <Input
            id="queue-slug"
            value={slug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value.toLowerCase());
            }}
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Nur Kleinbuchstaben, Ziffern und Bindestriche.
          </p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="queue-team">Team</Label>
            {canManageTeams ? (
              <Link
                to={TEAMS_ADMIN_PATH}
                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Teams verwalten
              </Link>
            ) : null}
          </div>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger id="queue-team" className="w-full">
              <SelectValue placeholder="Kein Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Kein Team</SelectItem>
              {activeTeams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                  {!team.isActive ? ' (archiviert)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {teamId === NONE ? (
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Ohne Team erreichen neue Tickets vor allem IT-Leitung/Admin — besser ein Support-Team
              zuweisen.
            </p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="queue-assignee">Standard-Bearbeiter</Label>
          <Select value={defaultAssigneeId} onValueChange={setDefaultAssigneeId}>
            <SelectTrigger id="queue-assignee" className="w-full">
              <SelectValue placeholder="Keine Person" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine Person</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {mode === 'edit' && (
          <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="queue-active">Aktiv</Label>
              <p className="text-xs text-muted-foreground">
                Inaktive Warteschlangen erscheinen nicht auf dem Board.
              </p>
            </div>
            <Switch id="queue-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={busy || !canSubmit}
          onClick={() => {
            const team = teamId === NONE ? null : teamId;
            const assignee = defaultAssigneeId === NONE ? null : defaultAssigneeId;
            if (mode === 'create') {
              void onSubmit({
                create: {
                  name: name.trim(),
                  slug: slug.trim(),
                  teamId: team,
                  defaultAssigneeId: assignee,
                },
              });
              return;
            }
            void onSubmit({
              update: {
                name: name.trim(),
                slug: slug.trim(),
                teamId: team,
                defaultAssigneeId: assignee,
                isActive,
              },
            });
          }}
        >
          {mode === 'create' ? 'Anlegen' : 'Speichern'}
        </Button>
      </DialogFooter>
    </>
  );
}
